import { Injectable, computed, signal } from '@angular/core';

export interface GlueArgLine {
  key: string;
  value: string;
  /** request = sent in POST body; ui = UI context not always in body; response = API response fields. */
  source: 'request' | 'ui' | 'response';
}

export interface ApiTraceRecord {
  id: string;
  kind: 'single' | 'poll-group';
  method: string;
  path: string;
  url: string;
  status: number;
  durationMs: number;
  capturedAt: string;
  requestText: string | null;
  responseText: string | null;
  errorText: string | null;
  pollCount: number;
  lastCapturedAt: string;
  /** Set for Glue validation / program-loader start & resume calls. */
  glueCategory?: 'glue-validation' | 'glue-program-loader';
  glueLabel?: string;
  /** run_id, log_id, S3 paths, load_id — parsed for quick reading. */
  glueSummary?: string | null;
  /** Every Glue argument from request body (+ UI context + response Glue fields). */
  glueArgs?: GlueArgLine[];
  /** Plain-English name for this API call (all entries). */
  callLabel?: string;
  /** read | write | glue | poll — for card styling. */
  callCategory?: 'read' | 'write' | 'glue' | 'poll';
}

const MAX_ENTRIES = 150;
const PREVIEW_CHARS = 120;

@Injectable({ providedIn: 'root' })
export class ValidationWorkbenchApiTraceService {
  private readonly enabled = signal(false);
  private readonly pageActive = signal(false);
  readonly hidePolling = signal(true);
  readonly drawerExpanded = signal(false);

  private readonly entries = signal<ApiTraceRecord[]>([]);
  private pendingGlueValidationContext: {
    activeLogId: number | null;
    action: string;
    assembledRequest?: Record<string, unknown>;
  } | null = null;
  private pendingProgramLoaderContext: {
    actionMode: string;
    resumeLoadId: number | null;
    assembledRequest?: Record<string, unknown>;
  } | null = null;

  readonly isActive = computed(() => this.enabled() && this.pageActive());

  readonly visibleEntries = computed(() => {
    const list = this.entries();
    if (!this.hidePolling()) return list;
    return list.filter(e => e.kind !== 'poll-group');
  });

  readonly pollSummaryEntries = computed(() =>
    this.entries().filter(e => e.kind === 'poll-group' && e.pollCount > 1)
  );

  readonly entryCount = computed(() => this.entries().length);

  setEnabled(value: boolean): void {
    this.enabled.set(!!value);
    if (!value) {
      this.clear();
    }
  }

  setPageActive(value: boolean): void {
    this.pageActive.set(!!value);
    if (value) {
      this.drawerExpanded.set(false);
    } else {
      this.clear();
    }
  }

  clear(): void {
    this.entries.set([]);
    this.pendingGlueValidationContext = null;
    this.pendingProgramLoaderContext = null;
  }

  /** Call immediately before POST /api/validation/glue/run. */
  setPendingGlueValidationContext(
    activeLogId: number | null,
    continueFromFailure: boolean,
    assembledRequest?: Record<string, unknown>
  ): void {
    if (!this.isActive()) return;
    this.pendingGlueValidationContext = {
      activeLogId,
      action: continueFromFailure ? 'continue/resume' : 'start',
      assembledRequest,
    };
  }

  /** Call immediately before POST /api/program-loader/glue/run. */
  setPendingProgramLoaderGlueContext(
    actionMode: string,
    resumeLoadId: number | null,
    assembledRequest?: Record<string, unknown>
  ): void {
    if (!this.isActive()) return;
    this.pendingProgramLoaderContext = {
      actionMode,
      resumeLoadId,
      assembledRequest,
    };
  }

  consumePendingGlueValidationContext(): {
    activeLogId: number | null;
    action: string;
    assembledRequest?: Record<string, unknown>;
  } | null {
    const ctx = this.pendingGlueValidationContext;
    this.pendingGlueValidationContext = null;
    return ctx;
  }

  consumePendingProgramLoaderContext(): {
    actionMode: string;
    resumeLoadId: number | null;
    assembledRequest?: Record<string, unknown>;
  } | null {
    const ctx = this.pendingProgramLoaderContext;
    this.pendingProgramLoaderContext = null;
    return ctx;
  }

  preview(text: string | null): string {
    if (text == null || text === '') return '—';
    const trimmed = text.trim();
    if (trimmed.length <= PREVIEW_CHARS) return trimmed;
    return `${trimmed.slice(0, PREVIEW_CHARS)}… (${formatBytes(trimmed.length)} total — use Copy)`;
  }

  async copyText(label: string, text: string | null): Promise<boolean> {
    if (text == null || text === '') return false;
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }

  formatFullExchange(entry: ApiTraceRecord, sequence: number): string {
    const payload = {
      sequence,
      label: entry.callLabel ?? entry.glueLabel ?? `${entry.method} ${entry.path}`,
      api: `${entry.method} ${entry.path}`,
      status: entry.status,
      durationMs: entry.durationMs,
      capturedAt: entry.lastCapturedAt,
      glueSummary: entry.glueSummary ?? undefined,
      glueArgs: entry.glueArgs ?? undefined,
      request: parseJsonLoose(entry.requestText),
      response: parseJsonLoose(entry.responseText ?? entry.errorText),
    };
    return JSON.stringify(payload, null, 2);
  }

  formatGlueArgs(entry: ApiTraceRecord): string {
    if (!entry.glueArgs?.length) {
      return parseJsonLoose(entry.requestText) != null
        ? JSON.stringify(parseJsonLoose(entry.requestText), null, 2)
        : '';
    }
    const grouped = {
      request: Object.fromEntries(
        entry.glueArgs.filter(a => a.source === 'request').map(a => [a.key, parseJsonLoose(a.value)])
      ),
      ui: Object.fromEntries(
        entry.glueArgs.filter(a => a.source === 'ui').map(a => [a.key, parseJsonLoose(a.value)])
      ),
      response: Object.fromEntries(
        entry.glueArgs.filter(a => a.source === 'response').map(a => [a.key.replace(/^response\./, ''), parseJsonLoose(a.value)])
      ),
    };
    return JSON.stringify(grouped, null, 2);
  }

  recordHttpEvent(input: {
    method: string;
    url: string;
    status: number;
    durationMs: number;
    requestBody: unknown;
    responseBody: unknown;
    errorMessage?: string;
  }): void {
    if (!this.isActive()) return;

    const path = pathFromUrl(input.url);
    if (path.includes('/api/debug/validation-workbench/event')) return;

    const requestText = serializeBody(input.requestBody);
    const responseText = input.errorMessage
      ? serializeBody(input.responseBody) || input.errorMessage
      : serializeBody(input.responseBody);
    const now = new Date().toISOString();
    const pollKey = pollGroupKey(input.method, path);

    if (pollKey) {
      const callMeta = describeApiCall(input.method, path);
      this.upsertPollGroup(pollKey, {
        method: input.method,
        path,
        url: input.url,
        status: input.status,
        durationMs: input.durationMs,
        capturedAt: now,
        requestText,
        responseText,
        errorText: input.errorMessage ?? null,
        callLabel: callMeta.callLabel,
        callCategory: callMeta.callCategory,
      });
      return;
    }

    const glueMeta = classifyGlueCall(
      path,
      requestText,
      responseText,
      () => this.consumePendingGlueValidationContext(),
      () => this.consumePendingProgramLoaderContext()
    );
    const callMeta = describeApiCall(input.method, path, glueMeta.glueLabel);

    const record: ApiTraceRecord = {
      id: nextId(),
      kind: 'single',
      method: input.method,
      path,
      url: input.url,
      status: input.status,
      durationMs: input.durationMs,
      capturedAt: now,
      requestText,
      responseText,
      errorText: input.errorMessage ?? null,
      pollCount: 1,
      lastCapturedAt: now,
      callLabel: callMeta.callLabel,
      callCategory: glueMeta.glueCategory ? 'glue' : callMeta.callCategory,
      ...glueMeta,
    };
    this.pushEntry(record);
  }

  private upsertPollGroup(
    pollKey: string,
    latest: Omit<ApiTraceRecord, 'id' | 'kind' | 'pollCount' | 'lastCapturedAt'> & { capturedAt: string }
  ): void {
    const list = [...this.entries()];
    const idx = list.findIndex(e => e.kind === 'poll-group' && e.id === pollKey);
    if (idx >= 0) {
      const prev = list[idx];
      list[idx] = {
        ...prev,
        status: latest.status,
        durationMs: latest.durationMs,
        responseText: latest.responseText,
        errorText: latest.errorText,
        pollCount: prev.pollCount + 1,
        lastCapturedAt: latest.capturedAt,
      };
    } else {
      list.unshift({
        id: pollKey,
        kind: 'poll-group',
        method: latest.method,
        path: latest.path,
        url: latest.url,
        status: latest.status,
        durationMs: latest.durationMs,
        capturedAt: latest.capturedAt,
        requestText: latest.requestText,
        responseText: latest.responseText,
        errorText: latest.errorText,
        pollCount: 1,
        lastCapturedAt: latest.capturedAt,
      });
    }
    this.entries.set(trimList(list));
  }

  private pushEntry(record: ApiTraceRecord): void {
    this.entries.set(trimList([record, ...this.entries()]));
  }
}

function trimList(list: ApiTraceRecord[]): ApiTraceRecord[] {
  return list.length > MAX_ENTRIES ? list.slice(0, MAX_ENTRIES) : list;
}

function nextId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function pathFromUrl(url: string): string {
  try {
    return new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined).pathname;
  } catch {
    return url;
  }
}

function serializeBody(body: unknown): string | null {
  if (body == null) return null;
  if (typeof body === 'string') return body;
  try {
    return JSON.stringify(body, null, 2);
  } catch {
    return String(body);
  }
}

function formatBytes(charCount: number): string {
  if (charCount < 1024) return `${charCount} B`;
  if (charCount < 1024 * 1024) return `${(charCount / 1024).toFixed(1)} KB`;
  return `${(charCount / (1024 * 1024)).toFixed(1)} MB`;
}

/** Collapse repeated progress polls into one rolling group per endpoint pattern. */
function pollGroupKey(method: string, path: string): string | null {
  if (method !== 'GET') return null;
  if (/\/api\/validation\/run\/[^/]+\/progress$/i.test(path)) {
    return `poll:validation-progress:${path}`;
  }
  if (/\/api\/program-loader\/glue\/(?:latest\/progress|\d+\/progress)$/i.test(path)) {
    return `poll:program-loader:${path}`;
  }
  return null;
}

function classifyGlueCall(
  path: string,
  requestText: string | null,
  responseText: string | null,
  consumeValidationCtx: () => {
    activeLogId: number | null;
    action: string;
    assembledRequest?: Record<string, unknown>;
  } | null,
  consumeProgramLoaderCtx: () => {
    actionMode: string;
    resumeLoadId: number | null;
    assembledRequest?: Record<string, unknown>;
  } | null
): Pick<ApiTraceRecord, 'glueCategory' | 'glueLabel' | 'glueSummary' | 'glueArgs'> {
  if (/\/api\/validation\/glue\/run$/i.test(path)) {
    const ui = consumeValidationCtx();
    const req = mergeRequestPayload(parseJson(requestText), ui?.assembledRequest);
    const res = parseJson(responseText);
    const glueArgs = buildValidationGlueArgs(req, res, ui);
    return {
      glueCategory: 'glue-validation',
      glueLabel: 'Glue validation (start / continue / resume)',
      glueSummary: glueArgsToSummary(glueArgs),
      glueArgs,
    };
  }
  if (/\/api\/program-loader\/glue\/run$/i.test(path)) {
    const ui = consumeProgramLoaderCtx();
    const req = mergeRequestPayload(parseJson(requestText), ui?.assembledRequest);
    const res = parseJson(responseText);
    const glueArgs = buildProgramLoaderGlueArgs(req, res, ui);
    return {
      glueCategory: 'glue-program-loader',
      glueLabel: 'Glue program loader (Copy/Transfer)',
      glueSummary: glueArgsToSummary(glueArgs),
      glueArgs,
    };
  }
  return {};
}

function parseJson(text: string | null): Record<string, unknown> | null {
  if (!text?.trim()) return null;
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function parseJsonLoose(text: string | null): unknown {
  if (text == null || text === '') return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function describeApiCall(
  method: string,
  path: string,
  glueLabel?: string
): Pick<ApiTraceRecord, 'callLabel' | 'callCategory'> {
  if (glueLabel) {
    return { callLabel: glueLabel, callCategory: 'glue' };
  }
  if (method === 'GET' && /\/api\/validation\/run\/[^/]+\/ui-state$/i.test(path)) {
    return { callLabel: 'Load validation UI state', callCategory: 'read' };
  }
  if (method === 'GET' && /\/api\/validation\/run\/[^/]+\/progress$/i.test(path)) {
    return { callLabel: 'Poll validation progress', callCategory: 'poll' };
  }
  if (method === 'POST' && /\/api\/validation\/run$/i.test(path)) {
    return { callLabel: 'Start validation (local/async)', callCategory: 'write' };
  }
  if (method === 'POST' && /\/api\/validation\/glue\/run$/i.test(path)) {
    return { callLabel: 'Glue validation (start / continue / resume)', callCategory: 'glue' };
  }
  if (method === 'POST' && /\/api\/program-loader\/glue\/run$/i.test(path)) {
    return { callLabel: 'Glue program loader (Copy/Transfer)', callCategory: 'glue' };
  }
  if (method === 'GET' && /\/api\/program-loader\/glue\/latest\/progress$/i.test(path)) {
    return { callLabel: 'Poll program-loader progress (latest)', callCategory: 'poll' };
  }
  if (method === 'GET' && /\/api\/program-loader\/glue\/\d+\/progress$/i.test(path)) {
    return { callLabel: 'Poll program-loader progress', callCategory: 'poll' };
  }
  if (method === 'GET' && /\/api\/validations\/grid$/i.test(path)) {
    return { callLabel: 'Load validation results grid', callCategory: 'read' };
  }
  if (method === 'POST' && /\/api\/validation\/run\/[^/]+\/recover-interrupted$/i.test(path)) {
    return { callLabel: 'Recover interrupted validation', callCategory: 'write' };
  }
  if (method === 'GET' && /\/api\/sgi-ui\/runs$/i.test(path)) {
    return { callLabel: 'Load runs list', callCategory: 'read' };
  }
  return {
    callLabel: `${method} ${shortPath(path)}`,
    callCategory: method === 'GET' ? 'read' : 'write',
  };
}

function shortPath(path: string): string {
  return path.length > 72 ? `${path.slice(0, 69)}…` : path;
}

const VALIDATION_GLUE_ARG_ORDER = [
  'run_id',
  'async',
  'continue_from_failure',
  'log_id',
  'validation_progress_batch_size',
  'source_path',
  'source_type',
  's3_staging_base_path',
  's3_run_folder_path',
  'output_run_folder_path',
  'final_source_output_path',
  'staged_after_sequence_path',
  'program_code',
  'upload_transfer_upload_guid',
  'upload_transfer_agent_name',
  'upload_transfer_sheet_name',
  'upload_transfer_staging_table',
  'enable_validation',
  'enable_status_tracking',
  'enable_master_enrichment',
  'apply_upload_transfer_header_mapping',
  'apply_cleanup_script',
  'load_results_to_postgres',
  'force_rebuild_staged_source',
  'final_source_output_enabled',
  'validation_progress_csv_enabled',
  'job_flow_csv_enabled',
] as const;

const PROGRAM_LOADER_GLUE_ARG_ORDER = ['run_id', 'load_id', 'dry_run'] as const;

function mergeRequestPayload(
  fromHttp: Record<string, unknown> | null,
  fromUi: Record<string, unknown> | undefined
): Record<string, unknown> | null {
  if (!fromHttp && !fromUi) return null;
  return { ...(fromUi ?? {}), ...(fromHttp ?? {}) };
}

function formatArgValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'boolean' || typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function orderedRequestKeys(req: Record<string, unknown>, preferred: readonly string[]): string[] {
  const seen = new Set<string>();
  const keys: string[] = [];
  for (const key of preferred) {
    if (key in req) {
      keys.push(key);
      seen.add(key);
    }
  }
  for (const key of Object.keys(req).sort()) {
    if (!seen.has(key)) keys.push(key);
  }
  return keys;
}

function buildValidationGlueArgs(
  req: Record<string, unknown> | null,
  res: Record<string, unknown> | null,
  ui: { activeLogId: number | null; action: string } | null
): GlueArgLine[] {
  const args: GlueArgLine[] = [];
  if (ui) {
    args.push({ key: 'action (UI)', value: ui.action, source: 'ui' });
    if (ui.activeLogId != null) {
      args.push({
        key: 'active_log_id (UI — not sent on Glue start unless in body)',
        value: String(ui.activeLogId),
        source: 'ui',
      });
    }
  }
  if (req) {
    for (const key of orderedRequestKeys(req, VALIDATION_GLUE_ARG_ORDER)) {
      args.push({ key, value: formatArgValue(req[key]), source: 'request' });
    }
  }
  if (res) {
    for (const key of Object.keys(res).sort()) {
      args.push({ key: `response.${key}`, value: formatArgValue(res[key]), source: 'response' });
    }
  }
  return args;
}

function buildProgramLoaderGlueArgs(
  req: Record<string, unknown> | null,
  res: Record<string, unknown> | null,
  ui: { actionMode: string; resumeLoadId: number | null } | null
): GlueArgLine[] {
  const args: GlueArgLine[] = [];
  if (ui) {
    args.push({ key: 'action_mode (UI)', value: ui.actionMode, source: 'ui' });
    if (ui.resumeLoadId != null) {
      args.push({ key: 'resume_load_id (UI)', value: String(ui.resumeLoadId), source: 'ui' });
    }
  }
  if (req) {
    for (const key of orderedRequestKeys(req, PROGRAM_LOADER_GLUE_ARG_ORDER)) {
      args.push({ key, value: formatArgValue(req[key]), source: 'request' });
    }
  }
  if (res) {
    for (const key of Object.keys(res).sort()) {
      args.push({ key: `response.${key}`, value: formatArgValue(res[key]), source: 'response' });
    }
  }
  return args;
}

function glueArgsToSummary(args: GlueArgLine[]): string {
  const requestParts = args
    .filter(a => a.source === 'request')
    .map(a => `${a.key}=${a.value.length > 80 ? `${a.value.slice(0, 77)}…` : a.value}`);
  const uiParts = args.filter(a => a.source === 'ui').map(a => `${a.key}=${a.value}`);
  const responseHighlights = args
    .filter(a => a.source === 'response' && /response\.(log_id|glue_job_run_id|glue_job_name|load_id|accepted|status|ok)/.test(a.key))
    .map(a => `${a.key.replace(/^response\./, '')}=${a.value}`);
  const parts = [...uiParts, ...requestParts, ...responseHighlights];
  return parts.length ? parts.join(' · ') : 'No Glue arguments captured.';
}
